import { useCallback, useEffect, useState } from 'react';
import { BasketParamsShapes, BearingHubHelper } from '@hub/libs/bearing-hub.helper';
import { WidenUtil } from '@hub/libs/widen-utils';
import { FALLBACK_DOMAIN, WIDEN_DEFAULT_IMAGE_QUALITY } from '@hub/libs/constants';
import { BuyBoxModals } from '@hub/common/BuyBox/modals/BuyBoxModals';
import * as Sentry from '@sentry/nextjs';
import type { Session } from '@hub/hooks/states/useSessionStore';
import type { i18n } from 'i18next';
import { useRouter } from 'next/router';
import type { ToastPosition } from '@hub/hooks/useToasts';
import { ToastType, useToasts } from '@hub/hooks/useToasts';
import { useTranslation } from 'next-i18next';
import { getDomainForLocale } from '@icommerce/i18n';
import { useNextHashQueryParams } from '@hub/hooks/useNextQueryParams';
import { updateSessionStore } from '@hub/libs/catalog-service';
import type { IbhBasketRequestDto, IbhIcnBasketRequestDto, IbhPartsResponseDto } from '@igus/iglidur-bearing-hub-api';
import { IbhShape, IbhUnitSystem } from '@igus/iglidur-bearing-hub-api';
import { addToBasket, updateBasket } from './basketRequests';
import { useLifetimeParametersStore } from '@hub/bearing-hub/libs/BearingsLifetime/useIbhLifetimeParametersValuesStore';
import getConfig from 'next/config';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import { useFeatureFlag } from '@hub/libs/abby/abby';
import type {
  IbhCatalogArticleResponseDto,
  IbhCustomArticleResponseDto,
  IbhIcnArticleResponseDto,
  IbhIcnPartsResponseDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { BasketToastSuccessContent } from '@hub/bearing-hub/common/basket/BasketToastSuccessContent';

const { publicRuntimeConfig } = getConfig() as NextRuntimeConfiguration;

export type CartArticleData =
  | IbhPartsResponseDto
  | IbhIcnPartsResponseDto
  | IbhCustomArticleResponseDto
  | IbhCatalogArticleResponseDto
  | IbhIcnArticleResponseDto;

export type UseAddToShoppingCartDefinition = {
  session: Session;
  shape: IbhShape;
  i18n: i18n;
  quantity: number;
  setupCostIsLoading?: boolean;
};
export const useAddToShoppingCart = ({
  session,
  quantity,
  shape,
  setupCostIsLoading,
  i18n,
}: UseAddToShoppingCartDefinition) => {
  const router = useRouter();
  const { addToast } = useToasts();
  const { t } = useTranslation(['common']);
  const useIcn = useFeatureFlag('ibh_useIcn');
  const { getLifetimeConfigPayload } = useLifetimeParametersStore();
  const [addToBasketInProgress, setAddToBasketInProgress] = useState(false);
  const [articlesAddingToBasket, setArticlesAddingToBasket] = useState<string[]>([]);
  const [pieces, setPieces] = useState(quantity);
  const [activeModal, setActiveModal] = useState<BuyBoxModals | null>(null);
  const shapeBearingParamsKey = BasketParamsShapes[shape];

  const query = useNextHashQueryParams();
  const cartPositionEditParams = query.edit as string;

  useEffect(() => {
    setPieces(quantity);
  }, [quantity]);

  const getImage = async (imageWidenShortKey: string) =>
    imageWidenShortKey
      ? BearingHubHelper.getBase64ImageFromURL(
          WidenUtil.generateWidenImageUrl({
            widenKey: imageWidenShortKey,
            width: 128,
            height: 128,
            name: imageWidenShortKey,
            quality: WIDEN_DEFAULT_IMAGE_QUALITY,
          })
        )
      : undefined;

  const getDimensionLabel = (label: string, partShape: IbhShape): string => {
    const labelMap: Record<string, string> = {
      b2d3: 'b2.2',
      b2d4: 'b2.1',
      slotAngle: 'T',
    };
    if (label === 'b1' && partShape === IbhShape.T) {
      return 's';
    }
    return labelMap[label] ?? label;
  };

  const getDimensionsTexts = (labels: string[], values: (number | object)[], unitSystem: IbhUnitSystem) =>
    labels.map((label: string, i) => {
      const dimensionLabel = getDimensionLabel(label, shape);
      const usedMetric = unitSystem === IbhUnitSystem.Imperial ? 'in' : 'mm';
      return `${dimensionLabel}: ${values[i]?.toLocaleString(i18n.language)} ${
        dimensionLabel === 'T' ? '°' : usedMetric
      }`;
    });

  const getIbhBasketRequestDto = async (
    articleData: CartArticleData,
    articleThumbnail?: string
  ): Promise<IbhBasketRequestDto | IbhIcnBasketRequestDto> => {
    const { unitPrice, dimensions, setupCost, articleNumber, unitSystem } = articleData;
    const widenShortKey = articleData.widenShortKey ?? articleData.widens[0].externalId;
    const {
      barStockArticle = undefined,
      userId = undefined,
      configurationId = undefined,
    } = articleData as IbhIcnPartsResponseDto;

    const unitPriceValue = Math.round((unitPrice.value + Number.EPSILON) * 100) / 100;
    const linkTargetUrl = router.asPath.replace(/[?&]edit=[A-F0-9]{7,}/g, '');
    const totalPriceValue = unitPrice.value * pieces + (setupCost?.value || 0);
    const domainForLocale = getDomainForLocale(
      router.locale,
      publicRuntimeConfig.envName === 'local' ? 'white' : publicRuntimeConfig.envName
    );
    const originUrl = domainForLocale ? `https://${domainForLocale}` : FALLBACK_DOMAIN;
    const generateDrawings = articleNumber.startsWith('TEMP-');
    const dimensionsInput = {
      [shapeBearingParamsKey]: {
        ...dimensions,
      },
    };
    const labels = Object.keys(dimensionsInput[shapeBearingParamsKey]);
    const values = Object.values(dimensionsInput[shapeBearingParamsKey]);
    const dimensionsTexts = getDimensionsTexts(labels, values, unitSystem);

    return {
      articleNumber,
      dimensions,
      shape,
      unitSystem,
      quantity: pieces,
      originUrl,
      generateDrawings,
      unitPrice: {
        unit: unitPrice.unit,
        value: unitPriceValue,
      },
      totalPrice: {
        unit: unitPrice.unit,
        value: Math.round((totalPriceValue + Number.EPSILON) * 100) / 100,
      },
      descriptionLines: [dimensionsTexts.map(str => str.replace(/\s+/g, '')).join(', ')],
      image: articleThumbnail ?? (await getImage(widenShortKey)),
      data: linkTargetUrl,
      lifetimeConfig: getLifetimeConfigPayload(unitSystem),
      ...(configurationId !== undefined && { configurationId }),
      ...(userId !== undefined && { userId }),
      ...(barStockArticle !== undefined && { barStockArticle }),
      useIcn,
    };
  };

  const addToShoppingCart = useCallback(
    async (articleData: CartArticleData, articleThumbnail?: string) => {
      try {
        if (!setupCostIsLoading && !addToBasketInProgress) {
          setAddToBasketInProgress(true);
          setArticlesAddingToBasket(prev => [...prev, articleData.articleNumber]);
          const ibhBasketRequestDto = await getIbhBasketRequestDto(articleData, articleThumbnail);

          ibhBasketRequestDto.generateDrawings && setActiveModal(BuyBoxModals.FILE_CREATION);

          const fetchIbdAddToBasketResponse = await (async () => {
            if (cartPositionEditParams) {
              return updateBasket(session, { ...ibhBasketRequestDto, cartPositionId: cartPositionEditParams });
            }

            return addToBasket(session, ibhBasketRequestDto);
          })();
          if (fetchIbdAddToBasketResponse.cartArticleCount > 0) {
            setActiveModal(BuyBoxModals.ADD_TO_SHOPPING_CART);
            setAddToBasketInProgress(false);
            setArticlesAddingToBasket(prev => prev.filter(request => request !== articleData.articleNumber));
            try {
              // Update session store after adding items to shopping cart.
              // Shopping cart item count in header should be updated automatically after state change
              await updateSessionStore();
              addToast({
                content: <BasketToastSuccessContent articleData={articleData} shape={shape} />,
                type: ToastType.INFO,
                position: 'relative top-72 mb-2 left-[calc(100vw-36rem)] w-[35rem]' as ToastPosition,
                allowMultiple: true,
              });
            } catch (error) {
              Sentry.captureException(new Error(`Could not refetch shopping cart in header - ${error as string}`));
            }
          } else {
            throw new Error('Adding or updating basket response was not valid!');
          }
        }
      } catch (error) {
        addToast({
          content: t('NOTIFICATION.ADD_TO_CART_FAILURE', {
            quantity,
            material: articleData.material.name,
            shape: t(`SHAPES.${shape}.SHAPE_MENU`, { ns: 'bearing-hub/bearingHub' }),
            ns: 'bearing-hub/bearingHub',
          }),
          type: ToastType.ERROR,
          position: 'top-72 right-0 items-center max-w-[45rem]' as ToastPosition,
        });
        setAddToBasketInProgress(false);
        setArticlesAddingToBasket(prev => prev.filter(request => request !== articleData.articleNumber));
        Sentry.captureException(
          new Error(`Could not ${cartPositionEditParams ? 'update' : 'add'} to basket - ${error as string}`)
        );
      }
    },
    [cartPositionEditParams, pieces, setupCostIsLoading, router, session]
  );

  return {
    addToBasketInProgress,
    setAddToBasketInProgress,
    addToShoppingCart,
    cartPositionEditParams,
    activeModal,
    setActiveModal,
    articlesAddingToBasket,
  };
};
